import { Component, OnInit, NgZone } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { InformesRxService } from 'src/app/services/transacciones/informes-rx.service';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

@Component({
    selector: 'app-subir-informe-rx',
    templateUrl: './subir-informe-rx.component.html',
    styleUrls: ['./subir-informe-rx.component.scss'],
    standalone: false
})
export class SubirInformeRxComponent implements OnInit {

  public informeId: number = 0;
  public informeHtml: string = '';
  public nombrePaciente: string = '';
  public nombreDoctor: string = '';
  public nombreServicio: string = '';
  public codigoServicio: string = '';
  public numeroOrden: string = '';
  public imagenesServicio: string[] = [];

  public spinnerBoolean: boolean = false;
  public cargando: string = 'Obteniendo información, por favor espere...';

  public isRecording = false;
  public audioUrl: string = '';
  public audioFile: File | null = null;
  public audioMime: string = '';
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: BlobPart[] = [];
  private audioStream: MediaStream | null = null;
  public safeAudioUrl: SafeUrl | null = null;
  private recognition: any;
  public dictando = false;
  public textoTemporal = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private informesRxService: InformesRxService,
    private toastr: ToastrService,
    private sanitizer: DomSanitizer,
    private zone: NgZone
  ) {}

  ngOnInit(): void {
    const raw = this.route.snapshot.paramMap.get('id') || '';
    const id = Number(raw.substring(100, raw.length - 100));
    this.informeId = id;
    this.spinnerBoolean = true;

    const token = localStorage.getItem('token') || '';
    this.informesRxService.obtenerInforme(id, token).subscribe({
      next: (resp: any) => {
        this.spinnerBoolean = false;
        if (!resp?.rows?.length) {
          this.toastr.error('No se encontró el informe');
          this.router.navigateByUrl('sistema/informes-rx');
          return;
        }
        const row = resp.rows[0];
        this.nombrePaciente = row.NOM_CLI || '';
        this.nombreDoctor   = row.NOM_DOC || '';
        this.nombreServicio = row.NOM_PRO || '';
        this.codigoServicio = row.CODN_PRO || '';
        this.numeroOrden    = row.NUM_ORD || '';
        this.imagenesServicio = this.extraerImagenes(row.DET_ORD, row.CODN_PRO);
      },
      error: () => {
        this.spinnerBoolean = false;
        this.toastr.error('No se encontró el informe');
        this.router.navigateByUrl('sistema/informes-rx');
      }
    });
  }

  guardarInforme(): void {
    if (!this.informeHtml.trim()) {
      this.toastr.warning('El informe no puede estar vacío');
      return;
    }

    this.spinnerBoolean = true;
    this.cargando = 'Guardando informe, por favor espere...';
    const token = localStorage.getItem('token') || '';

    this.informesRxService.guardarInforme(this.informeId, this.informeHtml, token).subscribe({
      next: () => {
        this.spinnerBoolean = false;
        this.toastr.success('Informe guardado correctamente', 'Éxito', {
          timeOut: 5000, closeButton: true, progressBar: true
        });
        this.router.navigateByUrl('sistema/informes-rx');
      },
      error: () => {
        this.spinnerBoolean = false;
        this.toastr.error('No se pudo guardar el informe');
      }
    });
  }

  volverAtras(): void {
    this.router.navigateByUrl('sistema/informes-rx');
  }

  private extraerImagenes(detOrd: string, codigoServicio: string): string[] {
    try {
      const detalle: any[] = JSON.parse(detOrd || '[]');
      const servicio = detalle.find(s => s.codigo === codigoServicio);
      return (servicio?.imagenesSubidas || []).map((img: any) => img.imagen).filter(Boolean);
    } catch {
      return [];
    }
  }

  /* ---------- GRABACIÓN DE AUDIO ---------- */
  async startRecording(): Promise<void> {
    try {
      this.audioChunks = [];
      this.audioFile = null;
      this.audioUrl = '';
      this.audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.mediaRecorder = new MediaRecorder(this.audioStream);
      this.isRecording = true;

      this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data?.size > 0) this.audioChunks.push(event.data);
      };

      this.mediaRecorder.onstop = () => {
        const blob = new Blob(this.audioChunks, { type: this.mediaRecorder?.mimeType || 'audio/webm' });
        this.audioMime = blob.type;
        const url = URL.createObjectURL(blob);
        this.audioUrl = url;
        this.safeAudioUrl = this.sanitizer.bypassSecurityTrustUrl(url);
        const ext = this.audioMime.includes('webm') ? 'webm' : this.audioMime.includes('ogg') ? 'ogg' : 'mp3';
        this.audioFile = new File([blob], `informe-audio.${ext}`, { type: this.audioMime });
        this.audioStream?.getTracks().forEach(t => t.stop());
        this.audioStream = null;
        this.isRecording = false;
      };

      this.mediaRecorder.start();
    } catch (err) {
      this.isRecording = false;
      this.toastr.error('No se pudo acceder al micrófono');
    }
  }

  stopRecording(): void {
    if (this.mediaRecorder && this.isRecording) this.mediaRecorder.stop();
  }

  clearRecording(): void {
    if (this.audioUrl) URL.revokeObjectURL(this.audioUrl);
    this.audioUrl = '';
    this.safeAudioUrl = null;
    this.audioFile = null;
    this.audioMime = '';
    this.audioChunks = [];
  }

  /* ---------- DICTADO POR VOZ ---------- */
  startDictation(): void {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) { alert('Tu navegador no soporta dictado de voz'); return; }

    this.recognition = new SR();
    this.recognition.lang = 'es-ES';
    this.recognition.interimResults = true;
    this.recognition.continuous = true;
    this.zone.run(() => (this.dictando = true));

    this.recognition.onresult = (event: any) => {
      let finalText = '';
      let interimText = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        if (res.isFinal) finalText += res[0].transcript + ' ';
        else interimText += res[0].transcript + ' ';
      }
      this.zone.run(() => {
        this.textoTemporal = this.normalizarPuntuacion(interimText.trim());
        if (finalText.trim()) {
          this.informeHtml = (this.informeHtml + ' ' + this.normalizarPuntuacion(finalText)).trim();
          this.textoTemporal = '';
        }
      });
    };

    this.recognition.onerror = () => this.zone.run(() => this.stopDictation());
    this.recognition.onend = () => { if (this.dictando) this.recognition.start(); };
    this.recognition.start();
  }

  stopDictation(): void {
    this.dictando = false;
    this.textoTemporal = '';
    try { this.recognition?.stop(); } catch {}
  }

  private normalizarPuntuacion(texto: string): string {
    return texto
      .replace(/\bcoma\b/gi, ',')
      .replace(/\bpunto\b/gi, '.')
      .replace(/\bdos puntos\b/gi, ':')
      .replace(/\bpunto y coma\b/gi, ';')
      .replace(/\bnueva línea\b/gi, '\n')
      .replace(/\bsigno de interrogación\b/gi, '?')
      .replace(/\bsigno de exclamación\b/gi, '!');
  }
}
